import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  activateGhlIntegration,
  deactivateGhlIntegration,
  fetchActiveIntegrationApp,
  getActivateIntegrationByUser,
  getIntegrationRefetchDataStatus,
  resetSelectedIntegrationData,
} from '../IntegrationSlice/IntegrationSlice'
import {Form, Button, Accordion, Card, Row, Col} from 'react-bootstrap'
import {isLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import {ExternalProvider} from '../IntegrationSlice/Types'
import {ENV} from '../../../../Utils/_helpers/EnvHelper'
import {Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useLazyGetSendersQuery} from '../../../../../services/cellcast.v2'
import Select from 'react-select'
import {
  useExportGhlContactMutation,
  useImportGhlContactMutation,
  useUpdateGhlSenderMutation,
} from '../../../../../services/cellcast'
import {SenderCamp} from '../../../../../services/cellcast.type'
import {getDefaultRegion} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {REGION} from '../../../../Utils/Constant/ConstantForWebsite'
import {useLazyGetActiveSenderQuery} from '../../../../../services/cellcast.v3'

interface GhlProps {
  selectedExternalProvider: ExternalProvider
  userData: any
}

const Ghl: FC<GhlProps> = ({selectedExternalProvider, userData}) => {
  const userRegion = useSelector(getDefaultRegion)

  let userId = ''
  if (userData && userData.user) {
    if (userData.user._id !== userData.user.ownerId && !userData.user.subClientAccountId) {
      userId = userData.user.ownerId
    } else {
      userId = userData.user._id
    }
  }

  const selectedActiveIntegration = useSelector(
    getActivateIntegrationByUser(userId, selectedExternalProvider._id)
  )

  const [isSubClient, setIsSubClient] = useState(false)
  const profileDetailState = useSelector((state: any) => state?.ProfileData?.ProfileData?.user)
  const refetch = useSelector(getIntegrationRefetchDataStatus)
  const globalLoading = useSelector(isLoading)

  const dispatch = useDispatch()

  const [selectedSmsSender, setSelectedSmsSender] = useState('#SharedNum#')
  const [selectedMmsSender, setSelectedMmsSender] = useState('#SharedNum#')

  const [selectedUsSmsSender, setSelectedUsSmsSender] = useState('')
  const [selectedUsMmsSender, setSelectedUsMmsSender] = useState('')

  // load sender options
  const [fetchSender, {data, isFetching: fetchSenderIsFetching, isLoading: fetchSenderIsLoading}] =
    useLazyGetSendersQuery()
  const smsOptions = data?.data?.sms || [{value: '#SharedNum#', label: '#SharedNum#'}]
  const mmsOptions = data?.data?.mms || [{value: '#SharedNum#', label: '#SharedNum#'}]

  const [
    fetchRegionSender,
    {
      data: regionSenderData,
      isFetching: fetchRegionSenderIsFetching,
      isLoading: fetchRegionSenderIsLoading,
    },
  ] = useLazyGetActiveSenderQuery()

  const defaultRegionOptions = [{value: '', label: 'Select Sender ID'}]

  const regionSmsOptions =
    regionSenderData?.data?.data &&
    Array.isArray(regionSenderData?.data?.data) &&
    regionSenderData?.data?.data?.length > 0
      ? regionSenderData?.data?.data?.map((s: any) => ({
          value: s.audNumber,
          label: `${s.numberType} - ${s.audNumber}`,
        }))
      : []

  const regionMmsOptions =
    regionSenderData?.data?.data &&
    Array.isArray(regionSenderData?.data?.data) &&
    regionSenderData?.data?.data?.length > 0
      ? regionSenderData?.data?.data
          .filter((s: any) => s.type === 'mms')
          .map((s: any) => ({value: s.audNumber, label: `${s.numberType} - ${s.audNumber}`}))
      : []

  const [updateSmsSender, {isLoading: updateSmsSenderLoading}] = useUpdateGhlSenderMutation()
  const [updateMmsSender, {isLoading: updateMmsSenderLoading}] = useUpdateGhlSenderMutation()

  const [importContact, {isLoading: importContactLoading}] = useImportGhlContactMutation()
  const [exportContact, {isLoading: exportContactLoading}] = useExportGhlContactMutation()

  // load data when first load
  useEffect(() => {
    if (selectedExternalProvider._id) {
      dispatch(resetSelectedIntegrationData)
      dispatch(fetchActiveIntegrationApp())
      if ([REGION.AU].includes(userRegion)) {
        fetchSender(null)
      } else {
        fetchRegionSender(null)
      }
    }
  }, [selectedExternalProvider])

  useEffect(() => {
    if (selectedActiveIntegration && selectedActiveIntegration.data) {
      if (selectedActiveIntegration.data.smsSender) {
        setSelectedSmsSender(selectedActiveIntegration.data.smsSender)
      }

      if (selectedActiveIntegration.data.mmsSender) {
        setSelectedMmsSender(selectedActiveIntegration.data.mmsSender)
      }

      if (selectedActiveIntegration.data.usSmsSender) {
        setSelectedUsSmsSender(selectedActiveIntegration.data.usSmsSender)
      }

      if (selectedActiveIntegration.data.usMmsSender) {
        setSelectedUsMmsSender(selectedActiveIntegration.data.usMmsSender)
      }
    }
  }, [selectedActiveIntegration])

  useEffect(() => {
    if (refetch) {
      dispatch(fetchActiveIntegrationApp())
    }
  }, [refetch])

  useEffect(() => {
    if (profileDetailState && profileDetailState.isSubClientAccount) {
      setIsSubClient(profileDetailState.isSubClientAccount)
    } else {
      setIsSubClient(false)
    }
  }, [profileDetailState])

  // handle delete
  const [isOpenDeactivateConfirmation, setIsOpenDeactivateConfirmation] = useState(false)
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)

  const onClickDeactivateHandler = () => {
    setIsOpenDeactivateConfirmation(true)
  }

  const onClickActivateHandler = () => {
    // Configurable base URL and client ID
    const baseUrl = ENV().REACT_APP_GHL_BASE_URL || 'https://marketplace.gohighlevel.com'
    const clientId = ENV().REACT_APP_GHL_CLIENT_ID
    const redirectUri = `${ENV().REACT_APP_FE_URL}/integration`
    const responseType = 'code'
    const scope =
      'conversations.readonly conversations.write conversations/message.readonly conversations/message.write contacts.readonly contacts.write workflows.readonly'
    // Construct the OAuth URL dynamically
    const oauthUrl = `${baseUrl}/oauth/chooselocation?response_type=${responseType}&redirect_uri=${redirectUri}&client_id=${clientId}&scope=${scope}`

    // Redirect the user to the OAuth provider's authorization screen
    const newWindow = window.open(oauthUrl, '_blank', 'width=800,height=600')

    const checkAuthCode = setInterval(() => {
      try {
        if (newWindow && newWindow.location && newWindow.location.search) {
          const urlParams = new URLSearchParams(newWindow.location.search)
          const code = urlParams.get('code')
          if (code) {
            dispatch(activateGhlIntegration({code})).then((res: any) => {
              if (res && res.payload && res.payload.error && res.payload.error.message) {
                toast.error(res.payload.error.message)
              } else {
                dispatch(fetchActiveIntegrationApp())
              }
            })
            clearInterval(checkAuthCode) // Stop the polling once we get the code
            newWindow.close() // Close the new tab after getting the code
          }
        }
      } catch (error) {}
    }, 1000)
  }

  const onUpdateSmsHandler = (region: string) => {
    let updatedSender
    switch (region) {
      case REGION.US:
        updatedSender = selectedUsSmsSender
        break
      default:
        updatedSender = selectedSmsSender
    }

    updateSmsSender({
      sender: updatedSender,
      type: SenderCamp.SMS,
      region: region.toLocaleLowerCase(),
    }).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
          dispatch(fetchActiveIntegrationApp())
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onUpdateMmsHandler = (region: string) => {
    let updatedSender
    switch (region) {
      case REGION.US:
        updatedSender = selectedUsMmsSender
        break
      default:
        updatedSender = selectedMmsSender
    }

    updateMmsSender({
      sender: updatedSender,
      type: SenderCamp.MMS,
      region: region.toLocaleLowerCase(),
    }).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
          dispatch(fetchActiveIntegrationApp())
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onImportContactHandler = () => {
    importContact(null).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onExportContactHandler = () => {
    exportContact(null).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const confirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenDeactivateConfirmation}
        handleClose={() => setIsOpenDeactivateConfirmation(false)}
        Message={'Are you sure, you want to deactivate integration ?'}
        confirm={() => setIsDeleteConfirm(true)}
        confirmBtnTitle='Yes, deactivate'
      />
    ),
    [isOpenDeactivateConfirmation]
  )

  useEffect(() => {
    if (isDeleteConfirm === true) {
      dispatch(deactivateGhlIntegration())
      setIsDeleteConfirm(false)
      setIsOpenDeactivateConfirmation(false)
    }
  }, [isDeleteConfirm])

  return (
    <div className='card p-7 pb-lg-4 p-lg-10'>
      {confirmation}
      <h1>Detail Integration</h1>
      <div className='row'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            <div className='me-7 mb-4'>
              <div
                className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative'
                style={{
                  backgroundColor: '#f5f8fa',
                  borderColor: '#f5f8fa',
                  borderRadius: 5,
                  paddingLeft: 5,
                  paddingRight: 5,
                  borderWidth: 1,
                }}
              >
                {selectedExternalProvider?.image == '' || selectedExternalProvider == undefined ? (
                  <span
                    className='squere1'
                    style={{
                      width: '100px',
                      height: '100px',
                    }}
                  >{`${
                    selectedExternalProvider?.name !== undefined &&
                    selectedExternalProvider?.name !== ''
                      ? selectedExternalProvider?.name.split('')?.[0]?.toUpperCase() ?? 'C'
                      : 'EP'
                  }`}</span>
                ) : (
                  <img src={selectedExternalProvider.image} alt='zapier' />
                )}
                <div
                  className={`position-absolute translate-middle bottom-0 start-100 mb-6 rounded-circle border border-4 border-white h-20px w-20px ${
                    selectedActiveIntegration ? ' bg-success ' : 'bg-danger'
                  }`}
                ></div>
              </div>
            </div>
            <div className='flex-grow-1'>
              <span className='text-gray-800 fs-2 fw-bolder me-1 mb-10'>
                {selectedExternalProvider?.name ?? 'Undefined'}
              </span>
              {selectedActiveIntegration ? (
                <div className='mt-5'>
                  <Form.Group className='mb-10'>
                    <Form.Label>Location Id</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data?.locationId}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className='mb-10'>
                    <Form.Label>Cellcast Api Key Label</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.internalApiKeyName}
                    />
                    <div>
                      <span className='text-muted'>Visit </span>
                      <Link to='/accounts/api-keys'>
                        <b>api page</b>
                      </Link>
                      <span className='text-muted'> for more detail </span>
                    </div>
                  </Form.Group>

                  {[REGION.AU].includes(userRegion) ? (
                    <>
                      <Form.Group className='mb-10'>
                        <Form.Label>Sms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchSenderIsFetching || fetchSenderIsLoading}
                              onChange={(e) => setSelectedSmsSender(e?.value || '')}
                              value={
                                smsOptions.find((el) => el.value === selectedSmsSender) ||
                                smsOptions[0]
                              }
                              options={[...smsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateSmsSenderLoading}
                              onClick={() => onUpdateSmsHandler(REGION.AU)}
                            >
                              {updateSmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>

                      <Form.Group className='mb-10'>
                        <Form.Label>Mms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchSenderIsFetching || fetchSenderIsLoading}
                              onChange={(e) => setSelectedMmsSender(e?.value || '')}
                              value={
                                mmsOptions.find((el) => el.value === selectedMmsSender) ||
                                mmsOptions[0]
                              }
                              options={[...mmsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateMmsSenderLoading}
                              onClick={() => onUpdateMmsHandler(REGION.AU)}
                            >
                              {updateMmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>
                    </>
                  ) : null}

                  {[REGION.US].includes(userRegion) ? (
                    <>
                      <Form.Group className='mb-10'>
                        <Form.Label>Sms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchRegionSenderIsFetching || fetchRegionSenderIsLoading}
                              onChange={(e) => setSelectedUsSmsSender(e?.value || '')}
                              value={
                                [...defaultRegionOptions, ...regionSmsOptions].find(
                                  (el: any) => el.value === selectedUsSmsSender
                                ) || defaultRegionOptions
                              }
                              options={[...defaultRegionOptions, ...regionSmsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateSmsSenderLoading}
                              onClick={() => onUpdateSmsHandler(userRegion)}
                            >
                              {updateSmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>

                      <Form.Group className='mb-10'>
                        <Form.Label>Mms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchRegionSenderIsFetching || fetchRegionSenderIsLoading}
                              onChange={(e) => setSelectedUsMmsSender(e?.value || '')}
                              value={
                                [...defaultRegionOptions, ...regionMmsOptions].find(
                                  (el: any) => el.value === selectedUsMmsSender
                                ) || defaultRegionOptions
                              }
                              options={[...defaultRegionOptions, ...regionMmsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateMmsSenderLoading}
                              onClick={() => onUpdateMmsHandler(userRegion)}
                            >
                              {updateMmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>
                    </>
                  ) : null}

                  <span className='text-gray-800 fs-3 fw-bolder me-1 mt-10'>Sync Contact</span>

                  <Form.Group className='mb-5 mt-3'>
                    <Row>
                      <Col sm='6'>
                        <Form.Label>Import contacts from go high level</Form.Label>
                      </Col>
                      <Col sm='4'>
                        <Button
                          variant='info'
                          type='submit'
                          size='sm'
                          disabled={importContactLoading}
                          onClick={() => onImportContactHandler()}
                        >
                          {importContactLoading ? (
                            <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                          ) : (
                            'Import'
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form.Group>
                  {[REGION.AU].includes(userRegion) ? (
                    <Form.Group className='mb-5'>
                      <Row>
                        <Col sm='6'>
                          <Form.Label>Push contacts to go high level</Form.Label>
                        </Col>
                        <Col sm='4'>
                          <Button
                            variant='info'
                            type='submit'
                            size='sm'
                            disabled={exportContactLoading}
                            onClick={() => onExportContactHandler()}
                          >
                            {exportContactLoading ? (
                              <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                            ) : (
                              'Push Contacts'
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form.Group>
                  ) : null}

                  <Button
                    variant='danger'
                    type='submit'
                    disabled={globalLoading}
                    onClick={() => onClickDeactivateHandler()}
                  >
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Deactivate'
                    )}
                  </Button>
                </div>
              ) : (
                <div className='mt-20'>
                  <Button variant='primary' type='button' onClick={() => onClickActivateHandler()}>
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Activate'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {Ghl}
